import { queryOptions } from '@tanstack/react-query'
import type { GroupRoster, GroupRosterSummary } from '@/types/rosterTypes'

// Resolving a group is several Microsoft Graph calls, so the server memoises
// it. These queries hold results a little longer than the composer's member
// lookup does — the groups screen is browsed, not typed into, and re-resolving
// forty groups every time someone clicks between them would be wasteful.
const ROSTER_STALE_TIME = 5 * 60 * 1000

/** Every sendable group with its resolved recipient counts — the Groups tab's list. */
export const groupRostersQueryOptions = () =>
  queryOptions({
    queryKey: ['groupRosters'],
    queryFn: async (): Promise<Array<GroupRosterSummary>> => {
      const response = await fetch('/api/groupRosters')
      if (!response.ok) {
        throw new Error('Failed to load groups')
      }
      const data = await response.json()
      return data.groups ?? []
    },
    staleTime: ROSTER_STALE_TIME,
  })

/** One group's full roster: who gets the text, where each of them came from, who was taken out. */
export const groupRosterQueryOptions = (groupId: string) =>
  queryOptions({
    queryKey: ['groupRoster', groupId],
    queryFn: async (): Promise<GroupRoster> => {
      const response = await fetch(`/api/groupRoster?group=${encodeURIComponent(groupId)}`)
      if (!response.ok) {
        throw new Error('Failed to load this group')
      }
      const data = await response.json()
      return data.roster
    },
    enabled: !!groupId,
    staleTime: ROSTER_STALE_TIME,
  })

/**
 * Re-read from Microsoft 365, bypassing the server's cache.
 *
 * For the admin who has just filled in a missing mobile number and wants to
 * confirm it took — without this they'd be told the number is still missing
 * until the cache aged out, and reasonably conclude the fix hadn't worked.
 *
 * Pass a group id to refresh one group, or nothing to refresh the whole list.
 */
export const refreshRoster = async (groupId?: string): Promise<void> => {
  const url = groupId
    ? `/api/groupRoster?group=${encodeURIComponent(groupId)}&refresh=1`
    : '/api/groupRosters?refresh=1'
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error('Could not refresh from Microsoft 365')
  }
}
